import { useState, useEffect } from 'react'
import api from '../../core/services/api'

interface OffertaItem {
  id: string
  numero_offerta: string
  titolo: string
  cliente_nome: string
  valore_totale: number
  margine_percentuale: number
  created_at: string | null
}
interface ColonnaStato {
  items: OffertaItem[]
  count: number
  valore_totale: number
}
type Pipeline = Record<string, ColonnaStato>

const COLONNE: { key: string; label: string; color: string; bg: string; border: string }[] = [
  { key: 'bozza',      label: 'Bozza',         color: 'text-gray-600',   bg: 'bg-gray-50',   border: 'border-gray-200' },
  { key: 'in_corso',   label: 'In corso',       color: 'text-gray-600',   bg: 'bg-gray-50',   border: 'border-gray-200' },
  { key: 'in_revisione', label: 'In revisione', color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  { key: 'approvata',  label: 'Approvata',      color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  { key: 'in_attesa',  label: 'In attesa',      color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  { key: 'confermata', label: 'Confermata ✓',   color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200' },
  { key: 'rifiutata',  label: 'Rifiutata',      color: 'text-red-500',    bg: 'bg-red-50',    border: 'border-red-200' },
]

const fmt = (n: number) => n >= 1000 ? `€${(n / 1000).toFixed(0)}k` : `€${n.toFixed(0)}`

export default function PipelineOfferte() {
  const [data, setData] = useState<Pipeline | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/v1/strategia/pipeline')
      .then((r: { data: Pipeline }) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" /></div>
  if (!data) return <div className="text-center text-gray-400 py-16">Nessun dato disponibile</div>

  const totaleAttivo = COLONNE
    .filter(c => !['rifiutata'].includes(c.key))
    .reduce((s, c) => s + (data[c.key]?.valore_totale || 0), 0)

  const totaleConfermato = data['confermata']?.valore_totale || 0
  const totaleInAttesa = data['in_attesa']?.valore_totale || 0

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Pipeline totale attiva</p>
          <p className="text-2xl font-bold text-gray-900">{fmt(totaleAttivo)}</p>
        </div>
        <div className="bg-white border border-green-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Già confermate</p>
          <p className="text-2xl font-bold text-green-600">{fmt(totaleConfermato)}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">In attesa risposta</p>
          <p className="text-2xl font-bold text-amber-600">{fmt(totaleInAttesa)}</p>
        </div>
      </div>

      {/* Funnel bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Valore pipeline per fase</h3>
        <div className="space-y-2">
          {COLONNE.map(col => {
            const colData = data[col.key]
            if (!colData || colData.count === 0) return null
            const pct = totaleAttivo > 0 ? (colData.valore_totale / totaleAttivo) * 100 : 0
            return (
              <div key={col.key} className="flex items-center gap-3">
                <span className={`text-xs font-medium w-28 ${col.color}`}>{col.label}</span>
                <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                  <div
                    className={`h-full ${col.bg} border-r ${col.border} flex items-center px-2 transition-all`}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  >
                    <span className={`text-xs font-semibold whitespace-nowrap ${col.color}`}>{fmt(colData.valore_totale)}</span>
                  </div>
                </div>
                <span className="text-xs text-gray-400 w-12 text-right">{colData.count} off.</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Kanban */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {COLONNE.map(col => {
          const colData = data[col.key]
          if (!colData || colData.count === 0) return null
          return (
            <div key={col.key} className={`flex-shrink-0 w-64 rounded-xl border ${col.border} overflow-hidden`}>
              <div className={`${col.bg} px-3 py-2 border-b ${col.border}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold ${col.color}`}>{col.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full bg-white bg-opacity-70 font-medium ${col.color}`}>
                    {colData.count}
                  </span>
                </div>
                <p className={`text-sm font-bold ${col.color} mt-0.5`}>{fmt(colData.valore_totale)}</p>
              </div>
              <div className="divide-y divide-gray-100 bg-white max-h-80 overflow-y-auto">
                {colData.items.map(o => (
                  <div key={o.id} className="px-3 py-2 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-gray-400">{o.numero_offerta}</p>
                        <p className="text-xs font-medium text-gray-800 truncate">{o.titolo}</p>
                        <p className="text-xs text-gray-400 truncate">{o.cliente_nome}</p>
                      </div>
                      <span className="text-xs font-bold text-gray-700 whitespace-nowrap">{fmt(o.valore_totale)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
